import React, { useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence, useSpring, useReducedMotion } from "framer-motion";

const stages = [
  {
    grade: "Grade 1 — 4",
    age: "Ages 6 — 9",
    title: "Spark the love of learning.",
    body:
      "Playful phonics, story studios, number games, and hands-on science. We turn early lessons into moments kids look forward to.",
    img: "/images/journey-1.png",
    color: "#FCA311",
  },
  {
    grade: "Grade 5 — 8",
    age: "Ages 10 — 13",
    title: "Confidence in every subject.",
    body:
      "Concepts click fast with clear explanations, daily doubt support, and projects that connect class to real life.",
    img: "/images/journey-2.png",
    color: "#4ECDC4",
  },
  {
    grade: "Grade 9 — 11",
    age: "Ages 14 — 16",
    title: "Exam-ready, still curious.",
    body:
      "Focused prep, weekly mocks, and mentor check-ins keep momentum high while curiosity stays alive.",
    img: "/images/journey-3.png",
    color: "#FF6B6B",
  },
  {
    grade: "Grade 12 & beyond",
    age: "Ages 17+",
    title: "Launch with clarity.",
    body:
      "Board prep, entrance focus, and guidance on what comes next. We help students step out with clarity and confidence.",
    img: "/images/journey-4.png",
    color: "#0B132B",
  },
];

function StageBlock({ stage, index, onActive }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 75%", "end 25%"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.35, 1, 1, 0.35]);
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const smoothOpacity = useSpring(opacity, { stiffness: 45, damping: 28, mass: 1.1 });
  const smoothY = useSpring(y, { stiffness: 45, damping: 28, mass: 1.1 });

  React.useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      if (v > 0.3 && v < 0.85) onActive(index);
    });
  }, [scrollYProgress, index, onActive]);

  return (
    <div ref={ref} className="min-h-[100vh] flex items-center" data-testid={`stage-${index}`}>
      <motion.div style={{ opacity: smoothOpacity, y: smoothY }} className="max-w-xl">
        <div
          className="text-[11px] tracking-[0.25em] uppercase font-bold mb-4"
          style={{ color: stage.color }}
        >
          ✦ {stage.grade} · {stage.age}
        </div>
        <h3 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-[#0B132B] tracking-tighter leading-[0.95]">
          {stage.title}
        </h3>
        <p className="mt-6 text-lg text-[#3A506B] leading-relaxed">{stage.body}</p>
        <div className="mt-10 lg:hidden">
          <img
            src={stage.img}
            alt={stage.grade}
            className="w-full h-[48vh] sm:h-[54vh] object-cover object-center rounded-3xl shadow-[0_18px_45px_rgba(11,19,43,0.12)]"
          />
        </div>
        <div className="mt-8 flex items-center gap-3">
          <div className="font-display text-7xl font-black tracking-tighter" style={{ color: stage.color }}>
            {String(index + 1)}
          </div>
          <div className="text-xs tracking-widest uppercase text-[#758A9F]">
            of 4 phases
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function GrowthJourney() {
  const [active, setActive] = React.useState(0);
  const reduceMotion = useReducedMotion();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const rawNovaScale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);
  const rawNovaY = useTransform(scrollYProgress, [0, 1], [70, -70]);
  const rawNovaRotate = useTransform(scrollYProgress, [0, 1], [-0.6, 0.6]);
  const novaScale = useSpring(rawNovaScale, { stiffness: 120, damping: 26, mass: 0.6 });
  const novaY = useSpring(rawNovaY, { stiffness: 120, damping: 26, mass: 0.6 });
  const novaRotate = useSpring(rawNovaRotate, { stiffness: 120, damping: 26, mass: 0.6 });

  return (
    <section
      ref={containerRef}
      id="journey"
      data-testid="growth-journey"
      className="relative px-6 md:px-12 lg:px-16"
      style={{ background: "var(--bg-primary)" }}
    >
      {/* progress bar */}
      <motion.div
        style={{ width: progressWidth }}
        className="fixed top-0 left-0 h-[3px] bg-[#FF6B6B] z-40 origin-left"
      />

      <div className="max-w-[1400px] mx-auto py-20 md:py-28">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#FF6B6B] mb-4">
            ✦ The Nova journey
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-[#0B132B] tracking-tighter leading-[0.95]">
            From first-day nerves <br />
            to last-day cheers.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left sticky image */}
          <div className="hidden lg:block">
            <div className="sticky top-0 h-screen flex items-center">
              <div className="relative w-full">
                <div
                  className="absolute -top-6 -left-6 right-6 bottom-6 rounded-[2rem]"
                  style={{ background: stages[active].color, opacity: 0.18 }}
                />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.99 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(11,19,43,0.18)]"
                  >
                    <motion.img
                      style={
                        reduceMotion
                          ? undefined
                          : {
                              scale: novaScale,
                              y: novaY,
                              rotateZ: novaRotate,
                            }
                      }
                      src={stages[active].img}
                      alt={stages[active].grade}
                      data-testid={`stage-image-${active}`}
                      className="w-full h-[70vh] object-cover object-center"
                    />
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">Stage {active + 1}</div>
                        <div className="font-display text-3xl font-black tracking-tight">{stages[active].grade}</div>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur text-xs">
                        {stages[active].age}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Right scrollable text blocks */}
          <div>
            {stages.map((s, i) => (
              <StageBlock key={i} stage={s} index={i} onActive={setActive} />
            ))}

          </div>
        </div>
      </div>
    </section>
  );
}